import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';

/**
 * The studio's team, for the screens that have to put a name on something.
 *
 * Only the people who can still sign in: the API leaves out anybody whose
 * access has been revoked, because offering them is offering to file *new* work
 * under somebody who no longer works here. What is already stored is a
 * different question — a byline written before somebody left stays exactly as
 * it was, and the picker keeps showing it.
 *
 * No email here, and that is the API's choice as much as this one: a list every
 * signed-in editor can read should be a list of names and faces, not an address
 * book.
 */
export interface TeamMember {
  id: string;
  name: string;
  avatarUrl: string | null;
}

const TEAM_MEMBERS = gql`
  query TeamMembers {
    teamMembers {
      id
      name
      avatarUrl
    }
  }
`;

export const useTeamMembers = () =>
  useQuery({
    queryKey: ['teamMembers'],
    queryFn: async () =>
      (await gqlClient.request<{ teamMembers: TeamMember[] }>(TEAM_MEMBERS)).teamMembers,
  });

/**
 * The options a byline picker offers, given who is on the team and what this
 * post already says.
 *
 * Accounts, by id, since [Blog 11]: the byline is the person, and their name
 * and photo follow from that. The second half is the whole reason this is a
 * function rather than a `map`. The posts the design importer wrote say "Duy
 * Nguyen", "Mai Tran" and "Sonasi Team" and point at nobody, so a list built
 * from the team alone would leave each of them selecting nothing, and the
 * first save would quietly re-sign a published post to whoever happened to
 * be first in the list. Those keep what they say, as an option marked for
 * what it is, until somebody picks a person.
 *
 * "Sonasi Team" is not even a person. It is the shape a studio byline takes
 * when the author is the studio — so the odd one out is not an edge case to
 * tidy away.
 */
export const bylineOptions = (
  team: TeamMember[],
  current: { authorId: string | null; author: string },
): Array<{ value: string; label: string }> => {
  const options = team.map((member) => ({ value: member.id, label: member.name.trim() }));
  const written = current.author.trim();

  if (current.authorId !== null) {
    if (options.some((one) => one.value === current.authorId)) return options;
    /*
     * Linked to somebody the team query did not bring back. With a team on
     * screen that is an account since disabled — `teamMembers` leaves those
     * out, and the post keeps their name and photo — so it is marked as
     * such; with no team at all the query failed, and the post still knows
     * who wrote it. Either way, dropping to the team alone would let the
     * picker lose that.
     */
    const label = team.length > 0 ? `${written} · no longer on the team` : written;
    return [{ value: current.authorId, label }, ...options];
  }
  if (written === '') return options;
  // Words and no account. Marked, so it reads as "this is what the post
  // says" rather than as a colleague nobody recognises. Its value is the
  // empty one, which the save never sends: picking it back is leaving the
  // post as it was.
  return [{ value: '', label: `${written} · not linked to an account` }, ...options];
};
